import React from "react";
import { Link, useParams } from "react-router-dom";
import NavBar from "../components/layout/NavBar";
import Footer from "../components/layout/Footer";

import savoinimauxImgPreview from "../images/projects/savoinimaux-preview.png";
import test from "../images/projects/teste.png";
import pacteImgPreview from "../images/projects/pacte-preview.png"

// Pacte
import Pacte1 from "../images/projects/pacte/1-appstore.jpg"
import Pacte2 from "../images/projects/pacte/2-appstore.jpg"

export default function ProjectDetailPage() {
  const { slug } = useParams();

  const projects = {
    pacte: {
      title: "PACTE",
      category: "Application mobile",
      year: "2025",
      heroImage: pacteImgPreview || null,
      video: null,
      gallery: [Pacte1, Pacte2].filter(Boolean),
      intro:
        "PACTE est une application mobile pensée pour dynamiser le commerce local en réunissant fidélité digitale, actualités, événements, notifications et messagerie dans une seule expérience cohérente.",
      context:
        "Le projet est né d'une volonté de moderniser la relation entre commerçants et habitants, avec un outil plus fluide, plus centralisé et plus engageant.",
      goals: [
        "Créer une expérience mobile claire et moderne",
        "Centraliser plusieurs usages dans une seule application",
        "Faciliter la fidélisation et la communication locale",
      ],
      features: [
        "Carte de fidélité digitale",
        "Messagerie utilisateur / commerçant",
        "Actualités locales",
        "Événements",
        "Notifications push",
        "Dashboard d'administration",
      ],
      result:
        "Le projet a abouti à une application complète, pensée comme un véritable produit digital local, avec une attention forte portée à l'expérience utilisateur et à l'évolutivité.",
    },
    savoinimaux: {
      title: "Savoinimaux",
      category: "Site web",
      year: "2025",
      heroImage: savoinimauxImgPreview || null,
      video: null,
      gallery: [].filter(Boolean),
      intro:
        "Savoinimaux est un projet de refonte web visant à moderniser l'image de l'association et à proposer une présence en ligne plus lisible, plus crédible et plus actuelle.",
      context:
        "L'objectif était de sortir d'une image vieillissante pour proposer un site plus structuré, plus clair et plus cohérent avec les attentes actuelles.",
      goals: [
        "Moderniser l'identité visuelle",
        "Améliorer la lisibilité du site",
        "Renforcer la crédibilité globale de la structure",
      ],
      features: [
        "Refonte visuelle",
        "Nouvelle hiérarchie de contenus",
        "Design plus moderne",
        "Présence digitale plus professionnelle",
      ],
      result:
        "Le projet permet de repositionner l'association avec une image plus nette, plus sérieuse et mieux adaptée aux standards actuels du web.",
    },
    "aj-construction": {
      title: "AJ Construction",
      category: "Présence digitale",
      year: "2026",
      heroImage: test || null,
      video: null,
      gallery: [].filter(Boolean),
      intro:
        "AJ Construction est un accompagnement digital orienté visibilité locale, image de marque et cohérence de communication.",
      context:
        "Le besoin principal était d'améliorer la présence en ligne de l'entreprise et de renforcer sa crédibilité auprès d'une audience locale.",
      goals: [
        "Renforcer la visibilité locale",
        "Valoriser l'image professionnelle",
        "Créer une communication plus cohérente",
      ],
      features: [
        "Optimisation présence en ligne",
        "Stratégie de contenu",
        "Image professionnelle",
        "Visibilité locale",
      ],
      result:
        "Le projet a permis d'apporter une base plus solide à la communication digitale de l'entreprise et d'améliorer la perception globale de son activité.",
    },
  };

  const project = projects[slug];

  if (!project) {
    return (
      <div className="min-h-screen bg-white text-black font-asap">
        <NavBar />
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-20 pb-32">
          <p className="text-[11px] uppercase tracking-[0.18em] text-black/40 mb-5">Projet</p>
          <h1 className="text-[3rem] md:text-[5rem] font-bold leading-[1] tracking-[-0.04em] mb-6">
            Projet introuvable
          </h1>
          <p className="text-black/50 text-[16px] leading-relaxed mb-8 max-w-md">
            Le projet demandé n'existe pas encore ou n'est pas encore publié.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-3 bg-black text-white text-[13px] font-medium px-5 py-3 rounded-full hover:opacity-75 transition-opacity"
          >
            Retour aux projets <span>→</span>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const hasHero = Boolean(project.heroImage);
  const hasVideo = Boolean(project.video);
  const hasGallery = Array.isArray(project.gallery) && project.gallery.length > 0;
  const hasGoals = Array.isArray(project.goals) && project.goals.length > 0;
  const hasFeatures = Array.isArray(project.features) && project.features.length > 0;
  const hasResult = Boolean(project.result);

  return (
    <div className="min-h-screen bg-white text-black font-asap selection:bg-black selection:text-white">
      <NavBar />

      <div className="max-w-5xl mx-auto px-6 md:px-10">

        {/* HERO */}
        <section className="pt-20 pb-12">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-[13px] text-black/40 hover:text-black transition-colors mb-10"
          >
            <span>←</span> Projets
          </Link>

          <p className="text-[11px] uppercase tracking-[0.18em] text-black/40 mb-4">
            {project.category} — {project.year}
          </p>

          <h1 className="text-[3.2rem] md:text-[5.5rem] font-bold leading-[1] tracking-[-0.04em] mb-6">
            {project.title}
          </h1>

          {project.intro && (
            <p className="text-black/50 text-[16px] md:text-[19px] leading-relaxed max-w-xl">
              {project.intro}
            </p>
          )}
        </section>

        {/* HERO IMAGE */}
        {hasHero && (
          <section className="pb-10">
            <div className="rounded-[20px] overflow-hidden bg-[#f5f5f5]">
              <img
                src={project.heroImage}
                alt={project.title}
                className="w-full h-[300px] md:h-[500px] object-cover"
                loading="lazy"
              />
            </div>
          </section>
        )}

        {/* VIDEO */}
        {hasVideo && (
          <section className="pb-10">
            <div className="rounded-[20px] overflow-hidden bg-[#f5f5f5]">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full"
              >
                <source src={project.video} type="video/mp4" />
              </video>
            </div>
          </section>
        )}

        {/* CONTEXT + RESULT */}
        <section className="pb-16 grid md:grid-cols-2 gap-6">
          {project.context && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-black/35 mb-4 pb-4 border-b border-black/8">
                Contexte
              </p>
              <p className="text-black/60 text-[15px] leading-relaxed">
                {project.context}
              </p>
            </div>
          )}
          {hasResult && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-black/35 mb-4 pb-4 border-b border-black/8">
                Résultat
              </p>
              <p className="text-black/60 text-[15px] leading-relaxed">
                {project.result}
              </p>
            </div>
          )}
        </section>

        {/* GOALS */}
        {hasGoals && (
          <section className="pb-16">
            <p className="text-[11px] uppercase tracking-[0.18em] text-black/35 mb-8 pb-4 border-b border-black/8">
              Objectifs
            </p>
            <div className="space-y-5">
              {project.goals.map((goal, index) => (
                <div key={goal} className="flex items-start gap-5">
                  <span className="text-[11px] text-black/30 tabular-nums pt-0.5 w-5 shrink-0">
                    0{index + 1}
                  </span>
                  <p className="text-[16px] md:text-[18px] text-black/75 leading-snug">
                    {goal}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FEATURES */}
        {hasFeatures && (
          <section className="pb-16">
            <p className="text-[11px] uppercase tracking-[0.18em] text-black/35 mb-8 pb-4 border-b border-black/8">
              Fonctionnalités
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {project.features.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-[14px] text-black/60 py-3 border-b border-black/6"
                >
                  <span className="w-1 h-1 rounded-full bg-black/25 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* GALLERY */}
        {hasGallery && (
          <section className="pb-16">
            <p className="text-[11px] uppercase tracking-[0.18em] text-black/35 mb-8 pb-4 border-b border-black/8">
              Aperçu visuel
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {project.gallery.map((image, index) => (
                <div
                  key={`${project.title}-${index}`}
                  className="rounded-[16px] overflow-hidden bg-[#f5f5f5]"
                >
                  <img
                    src={image}
                    alt={`${project.title} visuel ${index + 1}`}
                    className="w-full h-[260px] object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="pb-28">
          <div className="rounded-[28px] bg-black text-white px-8 md:px-12 py-12 md:py-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-4">
                Un projet similaire ?
              </p>
              <h2 className="text-[2rem] md:text-[3rem] font-bold leading-[1] tracking-[-0.04em] max-w-sm">
                Parlons du vôtre
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-white text-black text-[13px] font-medium px-6 py-3.5 rounded-full hover:opacity-80 transition-opacity"
              >
                Nous contacter <span>→</span>
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center gap-3 border border-white/20 text-white text-[13px] font-medium px-6 py-3.5 rounded-full hover:bg-white hover:text-black transition-all"
              >
                Retour aux projets
              </Link>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
}